import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { has } from 'lodash';
import { refreshBillingSubscriptionApiRequest } from '@/services/api/api-services/billing';
import { putCampaignExternalStatusApiRequest } from '@/services/api/campaign-api';
import {
  postEntityArchiveApiRequest,
  postEntityRestoreApiRequest,
} from '@/services/api/entity-api';
import { useGetUsingType, useI18n } from '@/composables';
import { useTableQuery } from '@/composables/useTableQuery';
import helpers from '@/services/helpers';
import { $emitter } from '@/services/emitter';
import { useMenuBase } from '@/composables/useMenuBase';

type MenuBaseApi = ReturnType<typeof useMenuBase>;

export const useMenuReportDialogs = (menuBase: MenuBaseApi) => {
  const tableQueryApi = useTableQuery();
  const route = useRoute();
  const { t } = useI18n();
  const { getUsingType } = useGetUsingType();
  const {
    store,
    is,
  } = menuBase;

  const createCampaign = () => {
    $emitter.emit('open-dialog', { name: 'CampaignDialog', data: { mode: 'create' } });
  };

  const editCampaign = (id?: string) => {
    $emitter.emit('open-dialog', { name: 'CampaignDialog', data: { mode: 'edit', id } });
  };

  const duplicateCampaign = (id?: string) => {
    $emitter.emit('open-dialog', { name: 'CampaignDialog', data: { mode: 'duplicate', id } });
  };

  const bulkEditCampaigns = () => {
    $emitter.emit('open-dialog', { name: 'BulkEditCampaign' });
  };

  const updateCost = () => {
    $emitter.emit('open-dialog', { name: 'UpdateCost' });
  };

  const campaignLinks = (id?: string) => {
    $emitter.emit('open-dialog', { name: 'CampaignLinks', data: { id } });
  };

  const startStop = ({ id, buttonText }: { id: string; buttonText: string }) => {
    const status = () => {
      if (buttonText === t('Start')) return 'play';
      if (buttonText === t('Stop')) return 'stop';
    };

    const successMessage = () => {
      if (buttonText === t('Start')) return 'Campaign successfully started';
      if (buttonText === t('Stop')) return 'Campaign successfully stopped';
    };

    return putCampaignExternalStatusApiRequest(id, {
      status: status(),
    }).then((payload) => {
      $emitter.emit('update-report-row', {
        id,
        data: [
          {
            newData: payload.externalStatus,
            column: 'DT_RowCampaignExternalStatus',
          },
          {
            newData: payload.externalStatus,
            column: 'DT_RowCampaignExternalStatus',
          },
        ],
      });

      store.clearTableSelectedItems();
      $emitter.emit('toast-success', successMessage());
    }).catch((error) => Promise.reject(error));
  };

  const createOffer = () => {
    $emitter.emit('open-dialog', { name: 'OfferDialog', data: { mode: 'create' } });
  };

  const editOffer = (id?: string) => {
    $emitter.emit('open-dialog', { name: 'OfferDialog', data: { mode: 'edit', id } });
  };

  const duplicateOffer = () => {
    $emitter.emit('open-dialog', { name: 'OfferDialog', data: { mode: 'duplicate' } });
  };

  const bulkEditOffer = () => {
    $emitter.emit('open-dialog', { name: 'BulkEditOffer' });
  };

  const createLanding = () => {
    $emitter.emit('open-dialog', { name: 'LandingDialog', data: { mode: 'create' } });
  };

  const editLanding = (id?: string) => {
    $emitter.emit('open-dialog', { name: 'LandingDialog', data: { mode: 'edit', id } });
  };

  const bulkEditLanding = () => {
    $emitter.emit('open-dialog', { name: 'BulkEditLanding' });
  };

  const duplicateLanding = () => {
    $emitter.emit('open-dialog', { name: 'LandingDialog', data: { mode: 'duplicate' } });
  };

  const createFlow = () => {
    $emitter.emit('open-dialog', { name: 'FlowDialog', data: { mode: 'create' } });
  };

  const editFlow = (id?: string) => {
    $emitter.emit('open-dialog', { name: 'FlowDialog', data: { mode: 'edit', id } });
  };

  const duplicateFlow = () => {
    $emitter.emit('open-dialog', { name: 'FlowDialog', data: { mode: 'duplicate' } });
  };

  const createAffiliateNetwork = () => {
    $emitter.emit('open-dialog', { name: 'AffiliateNetworkDialog', data: { mode: 'create' } });
  };

  const editAffiliateNetwork = (id?: string) => {
    $emitter.emit('open-dialog', { name: 'AffiliateNetworkDialog', data: { mode: 'edit', id } });
  };

  const duplicateAffiliateNetwork = () => {
    $emitter.emit('open-dialog', { name: 'AffiliateNetworkDialog', data: { mode: 'duplicate' } });
  };

  const createTrafficSource = () => {
    $emitter.emit('open-dialog', { name: 'TrafficSourceDialog', data: { mode: 'create' } });
  };

  const editTrafficSource = (id?: string) => {
    $emitter.emit('open-dialog', { name: 'TrafficSourceDialog', data: { mode: 'edit', id } });
  };

  const duplicateTrafficSource = () => {
    $emitter.emit('open-dialog', { name: 'TrafficSourceDialog', data: { mode: 'duplicate' } });
  };

  const fromTemplate = () => {
    if (is('traffic-sources')) {
      $emitter.emit('open-dialog', { name: 'TrafficSourceFromTemplate' });
    }

    if (is('affiliate-networks')) {
      $emitter.emit('open-dialog', { name: 'AffiliateNetworkFromTemplate' });
    }
  };

  const importDialog = () => {
    const mode = is('landings') ? 'landings' : 'offers';
    $emitter.emit('open-dialog', { name: 'ImportDialog', data: { mode } });
  };

  const getArchiveTimerPromise = (timeout: number) =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(undefined);
      }, timeout);
    });

  const handleModalArchiveConfirm = (ids: string[]) => {
    const { type } = route.params;

    store.setLoading(true);

    postEntityArchiveApiRequest(type as string, ids)
      .then((response) => {
        refreshBillingSubscriptionApiRequest();
        if (response && has(response, 'data.payload.usedItems')) {
          for (const item of response.data.payload.usedItems || []) {
            const usingType = getUsingType({ type: item.usingType });
            const message = t('{0} is used by {1} {2}', [item.usedName, usingType, item.usingName]);

            $emitter.emit('toast-warning', message, {
              dontHide: true,
            });
          }

          $emitter.emit('refresh-report');
          store.clearTableSelectedItems();
        } else {
          return getArchiveTimerPromise(1000);
        }
      })
      .then(() => {
        $emitter.emit('refresh-report');
        store.clearTableSelectedItems();
      })
      .finally(() => {
        store.setLoading(false);

        helpers.afterBrowserRedraw(() => {});
      });
  };

  const archive = () => {
    const selectedItems = tableQueryApi.tableSelectedItems.value as Array<{ DT_RowId: string }>;
    const ids = selectedItems.map((item) => item.DT_RowId);

    $emitter.emit('open-dialog', {
      name: 'ConfirmDialog',
      data: {
        message: t('All selected items will be archived if you press OK. You can restore them anytime.'),
        onConfirm: () => handleModalArchiveConfirm(ids),
      },
    });
  };

  const restore = () => {
    const selectedItems = tableQueryApi.tableSelectedItems.value as Array<{ DT_RowId: string }>;
    const { type } = route.params;

    store.setLoading(true);

    postEntityRestoreApiRequest(
      type as string,
      selectedItems.map((item) => item.DT_RowId),
    )
      .then(() => {
        refreshBillingSubscriptionApiRequest();
        getArchiveTimerPromise(2000);
      })
      .finally(() => {
        $emitter.emit('refresh-report');
        store.clearTableSelectedItems();
      });
  };

  const dialogActionsEvents = computed(() => ({
    campaigns: {
      create: createCampaign,
      edit: editCampaign,
      duplicate: duplicateCampaign,
      bulkEdit: bulkEditCampaigns,
      updateCost,
      campaignLinks,
      startStop,
    },
    offers: {
      create: createOffer,
      edit: editOffer,
      duplicate: duplicateOffer,
      bulkEdit: bulkEditOffer,
    },
    landings: {
      create: createLanding,
      edit: editLanding,
      bulkEdit: bulkEditLanding,
      duplicate: duplicateLanding,
    },
    flows: {
      create: createFlow,
      edit: editFlow,
      duplicate: duplicateFlow,
    },
    affiliateNetworks: {
      create: createAffiliateNetwork,
      edit: editAffiliateNetwork,
      duplicate: duplicateAffiliateNetwork,
    },
    trafficSources: {
      create: createTrafficSource,
      edit: editTrafficSource,
      duplicate: duplicateTrafficSource,
    },
    common: {
      createFromTemplate: fromTemplate,
      archive,
      restore,
      import: importDialog,
    },
  }));

  return {
    dialogActionsEvents,
  };
};
